import React from 'react';
import { Box, SimpleGrid, Card, CardBody, VStack, HStack, Text, Skeleton, SkeletonText, Flex, Spacer, Divider } from '@chakra-ui/react';

interface VirtualizedContainerProps {
  children: React.ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  emptySubMessage?: string;
  columns?: { base: number; md: number; lg: number; xl: number };
  spacing?: number;
  minItemWidth?: string;
  maxItemWidth?: string;
  minItemHeight?: string;
  skeletonType?: 'predios' | 'infracciones';
}

const VirtualizedContainer: React.FC<VirtualizedContainerProps> = ({
  children,
  isLoading = false,
  isEmpty = false,
  emptyMessage = "No se encontraron elementos",
  emptySubMessage = "Intenta ajustar los filtros de búsqueda",
  columns = { base: 1, md: 2, lg: 3, xl: 4 },
  spacing = 4,
  minItemWidth = "300px",
  maxItemWidth = "500px",
  minItemHeight = "300px",
  skeletonType = 'predios'
}) => {

  // Renderizar skeleton loading
  if (isLoading) {
    return (
      <SimpleGrid 
        columns={columns}
        spacing={spacing}
        w="100%"
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <Card 
            key={index}
            shadow="md" 
            borderRadius="lg" 
            minW={minItemWidth}
            maxW={maxItemWidth}
            minH={minItemHeight}
          >
            <CardBody>
              {skeletonType === 'infracciones' ? (
                <VStack align="stretch" spacing={3}>
                  {/* Header */}
                  <Flex align="center">
                    <VStack align="start" spacing={0}>
                      <Skeleton height="24px" width="180px" />
                      <Skeleton height="16px" width="120px" mt={1} />
                    </VStack>
                    <Spacer />
                    <Skeleton height="16px" width="40px" />
                  </Flex>
                  <Divider />
                  
                  {/* Información Principal */}
                  <VStack align="stretch" spacing={2}>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box>
                        <Skeleton height="16px" width="60px" mb={1} />
                        <Skeleton height="16px" width="100px" />
                      </Box>
                      <Box>
                        <Skeleton height="16px" width="40px" mb={1} />
                        <Skeleton height="16px" width="80px" />
                      </Box>
                    </SimpleGrid>
                    
                    <SimpleGrid columns={2} spacing={3}>
                      <Box>
                        <Skeleton height="16px" width="40px" mb={1} />
                        <Skeleton height="16px" width="90px" />
                      </Box>
                      <Box>
                        <Skeleton height="16px" width="60px" mb={1} />
                        <Skeleton height="16px" width="110px" />
                      </Box>
                    </SimpleGrid>
                    
                    <Box>
                      <Skeleton height="16px" width="120px" mb={1} />
                      <SkeletonText mt={1} noOfLines={2} spacing={1} skeletonHeight={2} />
                    </Box>
                    
                    <Box>
                      <Skeleton height="16px" width="50px" mb={1} />
                      <Skeleton height="16px" width="140px" />
                    </Box>
                  </VStack>
                  
                  <Divider />
                  
                  {/* Estados */}
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Skeleton height="16px" width="50px" />
                      <Skeleton height="20px" width="70px" borderRadius="full" />
                    </HStack>
                    <HStack justify="space-between">
                      <Skeleton height="16px" width="60px" />
                      <Skeleton height="20px" width="80px" borderRadius="full" />
                    </HStack>
                  </VStack>
                  
                  <Divider />
                  
                  {/* Acciones */}
                  <HStack spacing={2} justify="center">
                    <Skeleton height="32px" width="70px" borderRadius="md" />
                    <Skeleton height="32px" width="60px" borderRadius="md" />
                  </HStack>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <Flex align="center">
                    <VStack align="start" spacing={0}>
                      <Skeleton height="24px" width="200px" />
                      <Skeleton height="16px" width="60px" mt={1} />
                    </VStack>
                  </Flex>
                  <Divider />
                  <VStack align="stretch" spacing={2}>
                    <Box>
                      <Skeleton height="16px" width="80px" mb={1} />
                      <SkeletonText mt={1} noOfLines={2} spacing={1} skeletonHeight={2} />
                    </Box>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box>
                        <Skeleton height="16px" width="60px" mb={1} />
                        <Skeleton height="16px" width="100px" />
                      </Box>
                      <Box>
                        <Skeleton height="16px" width="40px" mb={1} />
                        <Skeleton height="20px" width="80px" borderRadius="full" />
                      </Box>
                    </SimpleGrid>
                    <Box>
                      <Skeleton height="16px" width="140px" mb={1} />
                      <SkeletonText mt={1} noOfLines={2} spacing={1} skeletonHeight={2} />
                    </Box>
                  </VStack>
                  <Divider />
                  <HStack spacing={2} justify="center">
                    <Skeleton height="32px" width="120px" borderRadius="md" />
                  </HStack>
                </VStack>
              )}
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>
    );
  }

  // Renderizar mensaje vacío
  if (isEmpty) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} py={8}>
            <Text fontSize="lg" color="gray.500">
              {emptyMessage}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {emptySubMessage}
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  // Renderizar contenido normal
  return (
    <SimpleGrid 
      columns={columns}
      spacing={spacing}
      w="100%"
    >
      {children}
    </SimpleGrid>
  );
};

export default VirtualizedContainer;